import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';
import { api } from '../../services/api';

/**
 * D-331 Q3 (follows D-329 §2) - a customer opened by a Team Lead / Project Manager has no money fields yet. The card
 * says so ("חסרים נתונים כספיים") and offers "שליחת בקשה להשלמה" - a notice to the OTE manager and the customer's
 * Account Manager. Shown only when the server says the customer is missing them AND the viewer cannot complete them;
 * nothing (no sums) is read here, and nothing shows while the answer loads or when it fails.
 */
export default function CustomerMissingFinancials({ orgId, className = '' }: { orgId: string; className?: string }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const access = useQuery({
    queryKey: ['organization-financials-access', orgId],
    queryFn: () => api.getOrganizationFinancialsAccess(orgId).then((r) => r.data),
    enabled: !!orgId,
    retry: false,
  });
  const request = useMutation({
    mutationFn: () => api.requestOrganizationFinancials(orgId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['organization-financials-access', orgId] }),
  });
  const d = access.data;
  if (!access.isSuccess || !d || d.missing !== true || d.can_complete === true) return null;
  const sent = d.request_sent === true || request.isSuccess;
  return (
    <div role="status" className={`flex flex-wrap items-center justify-between gap-3 rounded-lg border border-warning-200 bg-warning-50 px-3 py-2.5 text-sm text-warning-800 ${className}`}>
      <p className="flex items-start gap-2">
        <AlertTriangle size={16} className="mt-0.5 shrink-0" aria-hidden="true" />
        <span>{t('organization.financialsMissingNotice')}</span>
      </p>
      {sent ? (
        <span className="text-xs font-medium">{t('engagementPage.financialsRequestSent')}</span>
      ) : (
        <button type="button" className="btn btn-secondary btn-sm" disabled={request.isPending} aria-busy={request.isPending || undefined} onClick={() => request.mutate()}>
          {t('engagementPage.requestFinancials')}
        </button>
      )}
    </div>
  );
}
